import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Navigate, useParams } from 'react-router-dom';
import { PAGES, PAGE_KEYS } from '../utils/pages';
// Full-screen branded boot splash shown while the workspace boots (permissions
// + first data). Covers the whole layout (incl. sidebar) so nothing half-loaded
// flashes. Fades in on mount, fades out when booting finishes.
const BootSplash = ({ loading }) => {
  const [render, setRender] = useState(loading);
  const [out, setOut] = useState(false);
  useEffect(() => {
    if (loading) { setRender(true); setOut(false); return; }
    if (render) {
      setOut(true);
      const t = setTimeout(() => setRender(false), 500); // match fade-out
      return () => clearTimeout(t);
    }
  }, [loading, render]);
  if (!render) return null;
  return (
    <div className={`cl-boot ${out ? 'out' : 'in'}`}>
      <div className="cl-boot-inner">
        <div className="cl-boot-logo">
          <div className="cl-boot-track" />
          <div className="cl-boot-ring" />
          <img src="/vetobase-icon.png" alt="Vetobase" data-noinvert />
        </div>
        <div className="cl-boot-name">Vetobase <span>AI</span></div>
        <span className="cl-boot-word">Loading</span>
      </div>
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const PermissionsContext = createContext({ role: 'Member', pages: [], readOnly: false, loading: true, can: () => false, refresh: () => {} });

export const usePermissions = () => useContext(PermissionsContext);

export const PermissionsProvider = ({ children }) => {
  const { workspaceUuid } = useParams();
  const [state, setState] = useState({ role: 'Member', pages: [], readOnly: false, loading: true });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/me?workspaceUuid=${workspaceUuid}`);
      const role = res.data?.role || 'Member';
      const pages = role === 'Admin' ? [...PAGE_KEYS] : (res.data?.pages || []);
      const readOnly = !!res.data?.readOnly;
      setState({ role, pages, readOnly, loading: false });
      // Keep these in sync for any legacy reads elsewhere.
      localStorage.setItem('userRole', role);
      localStorage.setItem('readOnly', readOnly ? '1' : '0');
    } catch (err) {
      // On failure, fall back to whatever the login stored (fail safe: no extra access).
      const role = localStorage.getItem('userRole') || 'Member';
      const readOnly = localStorage.getItem('readOnly') === '1';
      setState({ role, pages: role === 'Admin' ? [...PAGE_KEYS] : [], readOnly, loading: false });
    }
  }, [workspaceUuid]);

  useEffect(() => { setState(s => ({ ...s, loading: true })); load(); }, [load]);

  const can = useCallback(
    (pageKey) => state.role === 'Admin' || state.pages.includes(pageKey),
    [state.role, state.pages]
  );

  return (
    <PermissionsContext.Provider value={{ ...state, can, refresh: load }}>
      {children}
      <BootSplash loading={state.loading} />
    </PermissionsContext.Provider>
  );
};

// First page (in canonical order) the current user is allowed to open.
export const useFirstAllowedPath = () => {
  const { pages, role } = usePermissions();
  const allowed = PAGES.find(p => role === 'Admin' || pages.includes(p.key));
  return allowed ? allowed.path : null;
};

// Route guard: render children if the user can access ANY of `pages`. Used by
// the Orders page, which hosts the base Shopify tabs plus the WIP boards — each
// gated by its own permission, so holding any one of them grants the page.
export const RequireAnyPage = ({ pages, children }) => {
  const { can, loading, role, pages: myPages } = usePermissions();
  if (loading) return null;  // boot splash (in PermissionsProvider) covers the screen
  if ((pages || []).some(p => can(p))) return children;
  const fallback = PAGES.find(p => role === 'Admin' || myPages.includes(p.key));
  if (fallback) return <Navigate to={`../${fallback.path}`} replace />;
  return <Navigate to="/login" replace />;
};

// Route guard: render children only if the user can access `page`, else bounce
// to their first allowed page (or a clean "no access" panel).
export const RequirePage = ({ page, children }) => {
  const { can, loading, role, pages } = usePermissions();

  if (loading) return null;  // boot splash (in PermissionsProvider) covers the screen

  if (can(page)) return children;

  const fallback = PAGES.find(p => role === 'Admin' || pages.includes(p.key));
  if (fallback && fallback.key !== page) {
    return <Navigate to={`../${fallback.path}`} replace />;
  }

  // No allowed pages at all.
  return (
    <div className="ui-page">
      <div className="ui-empty" style={{ textAlign: 'center' }}>
        <div className="ui-empty-ic" style={{ margin: '0 auto 14px' }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>No access</h2>
        <p style={{ color: '#64748b', fontSize: 14, maxWidth: 380, margin: '0 auto' }}>
          You don't have permission to view this page. Ask a workspace administrator to grant you access.
        </p>
      </div>
    </div>
  );
};

export default PermissionsContext;
